import parseMoneyValue from '@utils/parseMoneyValue';

export const formatMoney = (value: number) =>
  parseMoneyValue(Number.isFinite(value) ? value : 0);

/** Keeps the sign, because a shrinking month is the thing worth noticing. */
export const formatSignedMoney = (value: number) => {
  if (!Number.isFinite(value) || value === 0) return formatMoney(0);

  return `${value > 0 ? '+' : '−'}${formatMoney(Math.abs(value))}`;
};

/**
 * Uma taxa, em percentual.
 *
 * A casa decimal é parâmetro porque as telas discordam com razão: uma taxa de pagamento vive entre 40% e
 * 90%, onde a segunda casa é ruído, e uma taxa sobre impressão de oferta pode ficar abaixo de 1%, onde
 * arredondar para uma casa apaga a diferença entre "quase ninguém" e "ninguém". O que não pode divergir é
 * a implementação — três cópias com guardas diferentes já produziram "NaN%" numa delas.
 */
export const formatRate = (rate: number, decimals = 1) =>
  `${(Number.isFinite(rate) ? rate * 100 : 0).toFixed(decimals)}%`;

/**
 * Bucket labels come back as the first day of the bucket, in the client's timezone, as 'YYYY-MM-DD'.
 *
 * Rendered by splitting the string rather than through `new Date(label)`: the label already IS the local
 * day, and parsing it back into a Date would read it as UTC midnight and then print it in the browser's
 * zone, which west of Greenwich lands on the previous day. A month bucket would be labelled with the month
 * before it.
 */
export const formatBucket = (label: string, isMonthly: boolean) => {
  const [year, month, day] = label.split('-');
  if (!year || !month || !day) return label || '—';

  return isMonthly ? `${month}/${year}` : `${day}/${month}/${year}`;
};

/**
 * A duration in seconds, in the largest unit that still says something.
 *
 * The API answers in seconds because that is the resolution it measures at, and a producer reading
 * "10.842 s" learns nothing. The unit is chosen per value rather than fixed for the column: a median of
 * four minutes and a median of three days are both normal answers for this report, and forcing either
 * into the other's unit hides the number that matters.
 *
 * One decimal, and never a rounded-up zero: anything under a minute stays in seconds, so a genuinely
 * instant conversion reads as such instead of as "0,0 min".
 */
export const formatDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;

  return `${(seconds / 86400).toFixed(1)} dias`;
};

/** Days, as the API already sends them - fractional, because elapsed time is not calendar days. */
export const formatDays = (days: number) =>
  Number.isFinite(days) ? `${days.toFixed(1)} dias` : '—';

/**
 * A formatted value, or a dash when there was nothing to measure.
 *
 * Zero is a real observation for every duration in these reports - a conversion on contact, a repeat the
 * same afternoon - so an empty cohort must not borrow it. `medianSeconds: 0` beside `converted: 0` is
 * the API saying "no observations", and printing it as "0 s" turns that into the strongest possible
 * claim: that everyone bought the instant they arrived.
 *
 * The count is passed rather than the value because the value cannot distinguish the two cases. That is
 * documented on the API's own interface, and this is where the documentation has to become behaviour.
 */
export const orDash = (observations: number, formatted: string) =>
  observations > 0 ? formatted : '—';
